/*
  Pre-selects the free gift's variant in Vitals' volume-discount tiers to match
  the product being bought (e.g. a Large / Khaki hammock gets the Large / Khaki
  cover instead of the cover's first variant). Loaded by main-product when the
  section has app blocks; does nothing on pages without a Vitals gift picker.

  Vitals only reacts to its own dropdown UI (its hidden input is output-only),
  so a match is applied by opening that dropdown and choosing the option, with
  the option list hidden meanwhile (`.vitals-gift-matching` in
  section-main-product.css). Matching is by option value names, so it works
  for any similar Vitals gift offer: an exact value beats a partial one
  ("Grey" -> "Grey", not "Grey Plush"; "Plush *NEW*" -> "Grey Plush"), and
  ties go to the first option in Vitals' list.

  A gift the customer picks themselves is kept until they choose a different
  product variant.

  When the customer picks a tier, the dropdown is then reopened in view, with
  the match selected, and left open until they pick a cover or click away.
*/
(function () {
  const WIDGET = '.vtl-ub-vd-widget';
  const GIFT_SELECT = '.Vtl-VdTierGift__Select';
  const HEADER = '.Vtl-Dropdown__Header';
  const PORTAL = '.Vtl-VolumeDiscountCards__VariantSelector-Portal';
  const OPTION = '.Vtl-Dropdown__Portal-List-Option';

  const TIER = '.Vtl-VolumeDiscountCards__TierItem';
  const REVEAL_WINDOW = 3000;

  let busy = false;
  let manualVariantId = null;
  let tierPickedAt = 0;

  // "Plush *NEW*" -> "plush"; "Grey  Plush" -> "grey plush"
  const normalize = (value) =>
    value
      .toLowerCase()
      .replace(/\*[^*]*\*|\([^)]*\)/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const scoreValue = (productValue, giftValue) => {
    if (!productValue || !giftValue) return 0;
    if (productValue === giftValue) return 3;
    const productWords = productValue.split(' ');
    const giftWords = giftValue.split(' ');
    if (productWords.every((word) => giftWords.includes(word))) return 2;
    return productWords.some((word) => giftWords.includes(word)) ? 1 : 0;
  };

  // Vitals labels gift variants by their title, e.g. "Large / Grey Plush".
  const scoreLabel = (label, productValues) =>
    label
      .split(' / ')
      .map(normalize)
      .reduce((total, giftValue) => total + Math.max(0, ...productValues.map((value) => scoreValue(value, giftValue))), 0);

  const selectedVariant = (widget) => {
    const script = widget.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
    try {
      return script ? JSON.parse(script.textContent) : null;
    } catch (error) {
      return null;
    }
  };

  const waitFor = (find, timeout = 1000) =>
    new Promise((resolve) => {
      const found = find();
      if (found) return resolve(found);
      const observer = new MutationObserver(() => {
        const match = find();
        if (match) {
          observer.disconnect();
          resolve(match);
        }
      });
      // `open` too: Safari opens/closes Vitals' list by toggling it on an
      // existing <dialog>, with no nodes added or removed.
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
      setTimeout(() => {
        observer.disconnect();
        resolve(find());
      }, timeout);
    });

  const openPortal = () => [...document.querySelectorAll(PORTAL)].find((portal) => portal.querySelector('dialog[open]'));

  const matchSelect = async (select, productValues) => {
    const header = select.querySelector(HEADER);
    if (!header || header.getAttribute('aria-disabled') === 'true') return;

    const scrollY = window.scrollY;
    const previousFocus = document.activeElement;
    document.documentElement.classList.add('vitals-gift-matching');

    try {
      header.click();
      // Generous wait: on a slow phone Vitals can open the list late, and it
      // must still open (and close) while it's hidden, not after.
      const portal = await waitFor(openPortal, 2500);
      if (!portal) return;

      let best = null;
      let bestScore = 0;
      portal.querySelectorAll(OPTION).forEach((option) => {
        if (option.getAttribute('aria-disabled') === 'true') return;
        const score = scoreLabel(option.textContent.trim(), productValues);
        if (score > bestScore) {
          best = option;
          bestScore = score;
        }
      });

      // Closing by picking an option (the match, or the one already selected)
      // works in every browser; clicking the header again doesn't close the
      // list in Safari, which left it open behind the hiding class.
      const choice = best || portal.querySelector(`${OPTION}[aria-selected="true"]`);
      if (choice) {
        choice.click();
      } else {
        header.click();
      }
      await waitFor(() => !openPortal());
    } finally {
      document.documentElement.classList.remove('vitals-gift-matching');
      if (previousFocus && previousFocus !== document.activeElement) previousFocus.focus({ preventScroll: true });
      if (window.scrollY !== scrollY) window.scrollTo(window.scrollX, scrollY);
    }
  };

  // Opens the selected tier's first gift dropdown in view, as a click on it would.
  const reveal = async (widget) => {
    const header = widget.querySelector(`${GIFT_SELECT} ${HEADER}`);
    if (!header || header.getAttribute('aria-disabled') === 'true' || openPortal()) return;
    header.click();
    await waitFor(openPortal);
  };

  const matchWidget = async (widget) => {
    const variant = selectedVariant(widget);
    if (!variant || busy) return;

    const revealAfter = tierPickedAt && Date.now() - tierPickedAt < REVEAL_WINDOW;
    const productValues = (variant.options || []).map(normalize);
    const selects =
      String(variant.id) === manualVariantId
        ? []
        : [...widget.querySelectorAll(GIFT_SELECT)].filter(
            (select) => select.dataset.giftMatchedFor !== String(variant.id)
          );
    if (!selects.length && !(revealAfter && widget.querySelector(GIFT_SELECT))) return;

    busy = true;
    try {
      for (const select of selects) {
        await matchSelect(select, productValues);
        select.dataset.giftMatchedFor = String(variant.id);
      }
      if (revealAfter) {
        tierPickedAt = 0;
        await reveal(widget);
      }
    } finally {
      busy = false;
    }
  };

  const watch = (widget) => {
    if (widget.dataset.giftMatchWatched) return;
    widget.dataset.giftMatchWatched = 'true';
    let timer;
    new MutationObserver(() => {
      if (busy) return;
      clearTimeout(timer);
      timer = setTimeout(() => matchWidget(widget), 150);
    }).observe(widget, { childList: true, subtree: true });
    matchWidget(widget);
  };

  // A trusted pick in a gift dropdown is the customer's choice: keep it for
  // the current product variant.
  const rememberManualPick = (event) => {
    if (busy || !event.isTrusted || !event.target.closest(`${PORTAL} ${OPTION}`)) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    const widget = document.querySelector(WIDGET);
    const variant = widget && selectedVariant(widget);
    if (variant) manualVariantId = String(variant.id);
  };
  document.addEventListener('click', rememberManualPick, true);
  document.addEventListener('keydown', rememberManualPick, true);

  // A trusted pick of a tier (not its gift dropdown) asks for the dropdown to
  // be shown once Vitals renders it.
  const rememberTierPick = (event) => {
    if (busy || !event.isTrusted) return;
    if (!event.target.closest(`${WIDGET} ${TIER}`) || event.target.closest(GIFT_SELECT)) return;
    tierPickedAt = Date.now();
  };
  document.addEventListener('click', rememberTierPick, true);

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      manualVariantId = null;
      document.querySelectorAll(WIDGET).forEach(matchWidget);
    });
  }

  // Vitals renders its widget after page load.
  const findWidgets = () => {
    const widgets = document.querySelectorAll(WIDGET);
    widgets.forEach(watch);
    return widgets.length > 0;
  };
  if (!findWidgets()) {
    const pageObserver = new MutationObserver(() => {
      if (findWidgets()) pageObserver.disconnect();
    });
    pageObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => pageObserver.disconnect(), 15000);
  }
})();
